<template>
  <div class="nav-menu-list">
    <!-- Топ без групп -->
    <div v-if="itemsWithoutGroups.length > 0"
         class="nav-section"
    >
      <NavMenuItem v-for="item in itemsWithoutGroups"
                   :key="item.text + '-nogroup'"
                   :text="$t(item.text)"
                   :icon="item.icon"
                   :to="item.routeName ? { name: item.routeName } : null"
                   :is-open="isOpen"
                   :is-active="isItemActive(item.routeName, item.paths, item.routeNames)"
                   :disabled="isItemDisabled(item)"
                   :group="item.group"
                   @click="handleItemClick(item)"
      />
    </div>

    <!-- Динамические группы сверху -->
    <div v-for="group in groupedTopItems"
         :key="group.key"
         class="nav-section"
    >
      <div v-if="isOpen"
           class="nav-group-header"
           @click="toggleGroup(group.key)"
      >
        <span class="group-title">
          {{ $t('leftSideMenu.' + group.key) }}
        </span>
        <v-icon name="carret"
                width="20"
                height="20"
                :rotate="groupStates[group.key] ? 180 : 0"
                stroke="rgba(14, 14, 34, 0.6)"
                action-color="transparent"
                class="group-caret"
        />
      </div>
      <div v-else
           class="nav-group-header-compact"
           @click="toggleGroup(group.key)"
      >
        <span class="group-title-compact">
          {{ $t('leftSideMenu.' + group.key) }}
        </span>
        <v-icon name="carret"
                width="12"
                height="12"
                :rotate="groupStates[group.key] ? 180 : 0"
                stroke="rgba(14, 14, 34, 0.6)"
                action-color="transparent"
                class="group-caret-compact"
        />
      </div>
      <transition name="group-items">
        <div v-if="groupStates[group.key]"
             class="group-items"
             :class="{ 'group-items-closed': !isOpen }"
        >
          <NavMenuItem v-for="item in group.items"
                       :key="item.text + '-' + group.key"
                       :text="$t(item.text)"
                       :icon="item.icon"
                       :to="item.routeName ? { name: item.routeName } : null"
                       :is-open="isOpen"
                       :is-active="isItemActive(item.routeName, item.paths, item.routeNames)"
                       :disabled="isItemDisabled(item)"
                       :group="item.group"
                       @click="handleItemClick(item)"
          />
        </div>
      </transition>
    </div>

    <!-- Низ -->
    <div v-if="bottomItems.length > 0"
         class="nav-section bottom-section"
         :class="{ 'is-open': isOpen }"
    >
      <NavMenuItem v-for="item in bottomItems"
                   :key="item.text + '-bottom'"
                   :text="$t(item.text)"
                   :icon="item.icon"
                   :to="item.routeName ? { name: item.routeName } : null"
                   :is-open="isOpen"
                   :is-active="isItemActive(item.routeName, item.paths, item.routeNames || [])"
                   :disabled="isItemDisabled(item)"
                   :group="item.group"
                   @click="handleItemClick(item)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import NavMenuItem from './NavMenuItem.vue';

const props = defineProps({
  items: { type: Array, required: true, default: () => [] },
  isOpen: { type: Boolean, default: true },
  userPermissions: { type: Array, default: () => [] },
});
const emit = defineEmits(['item-click']);
const route = useRoute();
const store = useStore();

const userRole = computed(() => {
  const r = store.getters.role;
  // console.log('NavMenuList: computed userRole:', r);
  return r;
});

// Состояния групп — динамически по ключам
const groupStates = ref({});

const toggleGroup = (groupName) => {
  groupStates.value[groupName] = !groupStates.value[groupName];
};
const handleItemClick = (item) => emit('item-click', item);

const isItemDisabled = (item) => {
  if (item.disabled) return true;

  // Check roles if defined
  if (item.roles?.length) {
    if (!item.roles.includes(userRole.value)) {
      return true;
    }
  }

  if (item.permissions?.length) {
    // TEMPORARY: Allow statistics for operator even if permission is missing
    if (userRole.value === 'operator' && (item.permissions.includes('CAN_READ_STATISTIC') || item.permissions.includes('CAN_READ_CATALOG_POINTS'))) {
      return false;
    }
    return !item.permissions.some((permission) => props.userPermissions.includes(permission));
  }
  return false;
};

// Фильтры
const visibleItems = computed(() => props.items.filter((item) => !isItemDisabled(item)));

const itemsWithoutGroups = computed(() => visibleItems.value.filter((item) => !item.group && item.place === 'top'));

const topGroupKeys = computed(() => {
  const keys = new Set(visibleItems.value.filter((item) => item.group && item.place === 'top').map((item) => item.group));
  return Array.from(keys);
});

const groupedTopItems = computed(() =>
  topGroupKeys.value.map((key) => ({
    key,
    items: visibleItems.value.filter((item) => item.group === key && item.place === 'top'),
  })),
);

const bottomItems = computed(() => visibleItems.value.filter((item) => item.place === 'bottom'));

// Активность
const isItemActive = (routeName, paths = [], routeNames = []) => {
  if (route.name === routeName) return true;
  if (routeNames?.length && routeNames.includes(route.name)) return true;
  if (paths?.length) {
    const currentPath = route.path;
    return paths.some((path) => {
      const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
      const normalizedCurrentPath = currentPath.startsWith('/') ? currentPath.slice(1) : currentPath;
      return normalizedCurrentPath.startsWith(normalizedPath);
    });
  }
  return false;
};

// Авто-открытие групп по активному маршруту
const shouldGroupBeOpen = (groupName) => {
  const groupItems = props.items.filter((item) => item.group === groupName);
  return groupItems.some((item) => {
    if (route.name === item.routeName) return true;
    if (item.routeNames?.length && item.routeNames.includes(route.name)) return true;
    if (item.paths?.length) {
      const currentPath = route.path;
      return item.paths.some((path) => {
        const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
        const normalizedCurrentPath = currentPath.startsWith('/') ? currentPath.slice(1) : currentPath;
        return normalizedCurrentPath.startsWith(normalizedPath);
      });
    }
    return false;
  });
};

const initGroupStates = () => {
  const next = {};
  topGroupKeys.value.forEach((key) => {
    next[key] = shouldGroupBeOpen(key);
  });
  groupStates.value = next;
};

onMounted(initGroupStates);
watch(() => [route.name, topGroupKeys.value.join('|')], initGroupStates);
</script>

<style lang="scss" scoped>
.nav-menu-list {
  display: flex;
  gap: 24px;
  flex-direction: column;
  width: 100%;
  height: 100%;

  .nav-section {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &.bottom-section {
      margin-top: auto;
      padding: 20px 0 0;
      border-top: 1px solid rgba(14, 14, 34, 0.1);
      /* разделитель top/bottom */

      display: flex;
      flex-direction: row;
      gap: 8px;

      &:not(.is-open) {
        flex-direction: column;
      }
    }
  }

  .nav-group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    /* без отступа у заголовков групп */
    height: 18px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover .group-title {
      color: rgba(14, 14, 34, 0.8);
    }

    .group-title {
      font-family: 'Inter', sans-serif;
      font-weight: 500;
      font-size: 15px;
      line-height: 1.2;
      color: rgba(14, 14, 34, 0.6);
      transition: color 0.2s ease;
    }

    .group-caret {
      transition: transform 0.3s ease;
    }
  }

  .nav-group-header-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    height: 18px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover .group-title-compact {
      color: rgba(14, 14, 34, 0.8);
    }

    .group-title-compact {
      font-family: 'Inter', sans-serif;
      font-weight: 500;
      font-size: 11px;
      line-height: 1.2;
      color: rgba(14, 14, 34, 0.6);
      transition: color 0.2s ease;
    }

    .group-caret-compact {
      transition: transform 0.3s ease;
    }
  }

  .group-items {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &.group-items-closed {
      gap: 2px;
    }
  }
}

// Transition animations
.group-items-enter-active,
.group-items-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.group-items-enter-from {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}

.group-items-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}

.group-items-enter-to,
.group-items-leave-from {
  opacity: 1;
  max-height: 200px;
  transform: translateY(0);
}
</style>
